import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { ChatState, initialState } from './chat.state';
import * as chatActions from './chat.actions';

const reducer: ActionReducer<ChatState, Action> = createReducer(
  initialState,
  on(
    chatActions.getMyChat,
    (state): ChatState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    chatActions.getMyChatSuccess,
    (state, { messages, supportChat }): ChatState => ({
      ...state,
      chat: structuredClone(supportChat),
      messages: structuredClone(messages),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    chatActions.getMyChatError,
    (state, { errorMessage }): ChatState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    chatActions.addChatMessage,
    (state, {}): ChatState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    chatActions.addChatMessageSuccess,
    (state, { message }): ChatState => ({
      ...state,
      loading: false,
      errorMessage: null,
      messageAttachmentsToAdd: {},
      messages: [...state.messages, structuredClone(message)],
    })
  ),
  on(
    chatActions.addChatMessageError,
    (state, { errorMessage }): ChatState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(chatActions.uploadAttachments, (state, { opts: { files } }): ChatState => {
    const filesAsObj = {} as any;
    files.forEach(file => {
      filesAsObj[file.fileOriginalName] = {
        error: null,
        loading: true,
        uploaded: false,
        userAsset: file,
      };
    });

    return {
      ...state,
      loadingAttachments: true,
      messageAttachmentsToAdd: {
        ...state.messageAttachmentsToAdd,
        ...filesAsObj,
      },
    };
  }),
  on(chatActions.uploadAttachmentsSuccess, (state, { opts: { uploadedFiles } }): ChatState => {
    const inStateObj = {} as any;
    Object.entries(uploadedFiles).forEach(([key, fileUploadResult]) => {
      inStateObj[key] = {
        uploaded: fileUploadResult.uploaded,
        loading: false,
        error: fileUploadResult.errorMessage || null,
        userAsset: fileUploadResult.userAsset || null,
      };
    });
    const messageAttachmentsToAdd: ChatState['messageAttachmentsToAdd'] = {
      ...state.messageAttachmentsToAdd,
      ...inStateObj,
    };

    return {
      ...state,
      loadingAttachments: Object.values(messageAttachmentsToAdd).some(attachment => attachment.loading),
      messageAttachmentsToAdd,
    };
  }),
  on(chatActions.uploadAttachmentsError, (state, { opts: { errorMessage } }): ChatState => {
    return {
      ...state,
      errorMessage,
      loadingAttachments: Object.values(state.messageAttachmentsToAdd).some(attachment => attachment.loading),
    };
  }),

  on(chatActions.removeAttachmentFromUploading, (state, { opts: { userAssetId, fileOriginalName } }): ChatState => {
    const messageAttachmentsToAdd: ChatState['messageAttachmentsToAdd'] = {
      ...state.messageAttachmentsToAdd,
      [fileOriginalName]: {
        ...state.messageAttachmentsToAdd[fileOriginalName],
        loading: true,
        uploaded: false,
      },
    };

    return {
      ...state,
      loadingAttachments: true,
      messageAttachmentsToAdd,
    };
  }),
  on(
    chatActions.removeAttachmentFromUploadingSuccess,
    (state, { opts: { userAssetId, fileOriginalName } }): ChatState => {
      const rItems = { ...state.messageAttachmentsToAdd };
      delete rItems[fileOriginalName];
      return {
        ...state,
        loadingAttachments: Object.values(rItems).some(attachment => attachment.loading),
        messageAttachmentsToAdd: { ...rItems },
      };
    }
  ),
  on(
    chatActions.removeAttachmentFromUploadingError,
    (state, { opts: { userAssetId, fileOriginalName } }): ChatState => {
      const messageAttachmentsToAdd: ChatState['messageAttachmentsToAdd'] = {
        ...state.messageAttachmentsToAdd,
        [fileOriginalName]: {
          ...state.messageAttachmentsToAdd[fileOriginalName],
          loading: false,
          uploaded: true,
          error: 'ERROR_REMOVING',
        },
      };

      return {
        ...state,
        loadingAttachments: Object.values(messageAttachmentsToAdd).some(attachment => attachment.loading),
        messageAttachmentsToAdd,
      };
    }
  )
);

export function chatReducer(state: ChatState, action: Action): ChatState {
  return reducer(state, action);
}
